import { useState, useEffect, useCallback, useRef } from 'react';
import { postService, commentService } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import PostCard from '@/components/feed/PostCard';
import PostComposer from '@/components/feed/PostComposer';
import { Skeleton, SkeletonAvatar, SkeletonLine } from '@/components/ui/Skeleton';
import { Modal } from '@/components/ui/Modal';
import { Plus } from 'lucide-react';
import { useRealtime } from '@/hooks/useRealtime';
import { ROUTES } from '@/constants';

type FeedFilter = 'for-you' | 'following' | 'latest';

export default function HomeFeed() {
  const { user, profile } = useAuth();
  const { toast } = useToast();
  const [filter, setFilter] = useState<FeedFilter>('latest');
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const inFlight = useRef(false);

  useRealtime();

  // NOTE: `loading` and `page` must NOT be dependencies here. Including them
  // changes the callback identity every time loading flips, which re-runs the
  // effect below and produces an infinite fetch loop.
  const loadPosts = useCallback(async (reset = false, targetPage = 1) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    const { data, error } = await postService.getPosts(filter, targetPage, 20);
    if (error) {
      toast('Failed to load feed', error.message, 'destructive');
    } else {
      if (reset) setPosts(data || []);
      else setPosts((prev) => [...prev, ...(data || [])]);
      setHasMore((data?.length || 0) === 20);
    }
    setLoading(false);
    inFlight.current = false;
  }, [filter, toast]);

  useEffect(() => {
    setPage(1);
    loadPosts(true, 1);
  }, [filter, loadPosts]);

  const loadMore = () => {
    if (!hasMore || inFlight.current) return;
    const nextPage = page + 1;
    setPage(nextPage);
    loadPosts(false, nextPage);
  };

  const handlePostCreated = () => {
    setIsComposerOpen(false);
    loadPosts(true);
  };

  const filters = [
    { key: 'for-you' as FeedFilter, label: 'For You' },
    { key: 'following' as FeedFilter, label: 'Following' },
    { key: 'latest' as FeedFilter, label: 'Latest' },
  ];

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 pt-4 flex flex-col gap-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 pt-4 space-y-4 min-h-screen">
      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
        {filters.map((f) => (
          <button
            key={f.label}
            onClick={() => setFilter(f.key)}
            className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
              filter === f.key
                ? 'bg-brand-600 text-white border-brand-600'
                : 'bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 border-neutral-200 dark:border-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-700'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {posts.length === 0 && (
        <div className="text-center py-12">
          <p className="text-neutral-500 dark:text-neutral-400">No posts yet. Be the first to share something!</p>
        </div>
      )}

      {posts.map((post) => (
        <PostCard key={post.id} post={post} />
      ))}

      {hasMore && (
        <button onClick={loadMore} className="w-full py-3 rounded-xl border border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors">
          Load more
        </button>
      )}

      {/* Floating action button for new post */}
      <button
        onClick={() => setIsComposerOpen(true)}
        className="fixed bottom-6 right-6 z-50 flex items-center justify-center rounded-full w-14 h-14 bg-brand-600 text-white shadow-lg transition hover:bg-brand-700 active:scale-95"
        aria-label="Create new post"
      >
        <Plus size={24} />
      </button>

      <Modal isOpen={isComposerOpen} onClose={() => setIsComposerOpen(false)}>
        <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
          New Post
        </h2>
        <PostComposer onPostCreated={handlePostCreated} />
      </Modal>
    </div>
  );
}
